<template>
  <!-- Armazón del diseño nuevo de la calculadora (#467, maqueta aprobada por Dirección el 09/10/2026).
       No sabe nada de RITE: pinta la cabecera, la barra de botones y las dos columnas, y lo de dentro
       lo pone quien lo usa. Es lo que permite que #470 lo lleve al resto de herramientas sin rehacerlo. -->
  <div class="cdn">
    <!-- Una sola franja baja, azul de lado a lado (Dirección, prueba del 26/10/2026): el alto es para
         el cálculo, no para la cabecera. Si los botones no caben en una fila, los menos usados pasan
         al menú «Más»; nunca se apilan. -->
    <header ref="bar" class="cdn-bar">
      <div class="cdn-id">
        <!-- El logotipo es azul y verde sobre blanco: sobre el azul de la franja no se leería, así
             que va en su propio recuadro blanco, dentro de la franja y separado de los bordes. -->
        <span ref="logo" class="cdn-logo"><img :src="logo" alt="SIMECAL"></span>
        <div class="cdn-who">
          <b :title="titulo">{{ titulo }}</b>
          <span v-if="subtitulo" :title="subtitulo">{{ subtitulo }}</span>
        </div>
      </div>
      <nav ref="tools" class="cdn-tools" aria-label="Acciones de la calculadora">
        <button class="cdn-tb ghost" type="button" @click="$emit('atras')">‹ Atrás</button>
        <!-- Desde una línea de oferta, «Atrás» cierra la calculadora (como siempre); y como al reabrir
             una línea ya calculada se entra directo en su herramienta, hace falta otra salida para
             calcular esa línea con una herramienta distinta. -->
        <button v-if="puedeCambiar" class="cdn-tb ghost" type="button" @click="$emit('cambiar')">Cambiar de herramienta</button>
        <button v-for="b in enFila" :key="b.ev" class="cdn-tb" type="button" @click="$emit(b.ev)">
          <span v-if="b.color" class="dot" :style="{ background: b.color }"></span>{{ b.label }}
        </button>
        <div v-if="compacto" ref="mas" class="cdn-mas">
          <button class="cdn-tb" type="button" aria-haspopup="true" :aria-expanded="masAbierto ? 'true' : 'false'" @click="masAbierto = !masAbierto">Más ▾</button>
          <div v-if="masAbierto" class="cdn-mas-menu" role="menu" @keydown.esc="masAbierto = false">
            <button v-for="b in enMas" :key="b.ev" type="button" role="menuitem" @click="elegir(b.ev)">
              <span class="dot" :style="{ background: b.color || 'transparent' }"></span>{{ b.label }}
            </button>
          </div>
        </div>
        <!-- Abierta desde el menú Tarifas no hay línea en la que escribir: el botón no se dibuja
             (CLAUDE.md §9, un punto de entrada que no lleva a ningún sitio no se pinta). Sin precio
             todavía, el botón sigue ahí pero dice por qué no guarda, junto a él, al pasar o pulsar:
             en una franja de 60 px no cabe la frase escrita al lado. -->
        <span v-if="puedeGuardar" class="cdn-guardar" @mouseenter="verMotivo = true" @mouseleave="verMotivo = false">
          <button class="cdn-tb primary" :class="{ off: !guardarListo }" type="button" :aria-disabled="guardarListo ? 'false' : 'true'"
            :title="guardarListo ? '' : motivoGuardar" @focus="verMotivo = true" @blur="verMotivo = false" @click="pulsarGuardar">Guardar en línea</button>
          <span v-if="!guardarListo && motivoGuardar && verMotivo" class="cdn-why" role="status">{{ motivoGuardar }}</span>
        </span>
      </nav>
    </header>

    <div class="cdn-body">
      <div class="cdn-calc">
        <div class="cdn-col"><slot></slot></div>
        <aside class="cdn-side"><slot name="resultado"></slot></aside>
      </div>
    </div>

    <slot name="extra"></slot>
  </div>
</template>

<script>
import logo from './../../../static/img/logo-simecal-2026.png';

// Los botones de consulta, en el orden de la maqueta. Los marcados `mas` son los que Dirección
// mandó al menú «Más» cuando la fila no cabe; Tutor IA se queda siempre a la vista.
const CONSULTAS = [
  { ev: 'info', label: 'Info', color: '#3B82F6', mas: true },
  { ev: 'norma', label: 'Norma', color: '#EF4444', mas: true },
  { ev: 'docs', label: 'Docs', color: '#22C55E', mas: true },
  { ev: 'conoc', label: 'Conoc', color: '#A855F7', mas: true },
  { ev: 'tutor', label: 'Tutor IA', color: '#C084FC', mas: false },
  { ev: 'pdf', label: 'PDF', color: '', mas: true },
  { ev: 'guia', label: 'Guía', color: '', mas: true }
];
// Lo mínimo que se le deja al título a la izquierda antes de mandar botones a «Más».
const TITULO_MIN = 170;

export default {
  props: {
    titulo: { type: String, default: '' },
    subtitulo: { type: String, default: '' },
    // Si existe una línea de oferta en la que guardar. Sin ella el botón no existe.
    puedeGuardar: { type: Boolean, default: false },
    // Si hay ya algo que guardar. Con el botón visible pero sin precio, se dice por qué al lado.
    guardarListo: { type: Boolean, default: true },
    motivoGuardar: { type: String, default: '' },
    puedeCambiar: { type: Boolean, default: false }
  },
  data: function () {
    // El logotipo vertical a color que pide la maqueta es byte a byte el `logo-simecal-2026.png` que
    // ya estaba en el repo: se usa ese en vez de meter una segunda copia del mismo fichero.
    return { logo: logo, compacto: false, anchoFila: 0, masAbierto: false, verMotivo: false };
  },
  computed: {
    enFila: function () {
      const compacto = this.compacto;
      return CONSULTAS.filter(function (b) { return !compacto || !b.mas; });
    },
    enMas: function () { return CONSULTAS.filter(function (b) { return b.mas; }); }
  },
  watch: {
    // Lo que cambia el ancho de la fila obliga a volver a medirla entera.
    puedeGuardar: 'remedir',
    puedeCambiar: 'remedir',
    masAbierto: function (v) {
      if (v) document.addEventListener('mousedown', this.fueraDeMas, true);
      else document.removeEventListener('mousedown', this.fueraDeMas, true);
    }
  },
  mounted: function () {
    this.alCambiarTamano = this.medir.bind(this);
    // La franja cambia de ancho sin que cambie la ventana (al plegar el menú lateral, o dentro de la
    // ventana de la oferta): donde se puede, se observa la propia franja.
    if (typeof window.ResizeObserver === 'function') {
      this.observador = new window.ResizeObserver(this.alCambiarTamano);
      this.observador.observe(this.$refs.bar);
    } else {
      window.addEventListener('resize', this.alCambiarTamano);
    }
    this.$nextTick(this.medir);
  },
  beforeDestroy: function () {
    if (this.observador) this.observador.disconnect();
    else window.removeEventListener('resize', this.alCambiarTamano);
    document.removeEventListener('mousedown', this.fueraDeMas, true);
  },
  methods: {
    /**
     * Decide si los botones caben en una fila. El ancho de la fila completa sólo se puede medir con
     * la fila completa a la vista, así que se guarda la última medida y con ella se decide al
     * ensanchar; `remedir` la vuelve a tomar cuando cambian los botones.
     */
    medir: function () {
      const bar = this.$refs.bar;
      const tools = this.$refs.tools;
      if (!bar || !tools) return;
      if (!this.compacto) this.anchoFila = tools.scrollWidth;
      const estilo = window.getComputedStyle(bar);
      const util = bar.clientWidth - (parseFloat(estilo.paddingLeft) || 0) - (parseFloat(estilo.paddingRight) || 0);
      const logoAncho = this.$refs.logo ? this.$refs.logo.offsetWidth : 0;
      const compacto = this.anchoFila + logoAncho + TITULO_MIN + 24 > util;
      if (compacto !== this.compacto) {
        this.compacto = compacto;
        if (!compacto) this.masAbierto = false;
      }
    },
    remedir: function () {
      this.compacto = false;
      this.masAbierto = false;
      this.$nextTick(this.medir);
    },
    elegir: function (ev) {
      this.masAbierto = false;
      this.$emit(ev);
    },
    fueraDeMas: function (e) {
      if (this.$refs.mas && !this.$refs.mas.contains(e.target)) this.masAbierto = false;
    },
    pulsarGuardar: function () {
      if (this.guardarListo) this.$emit('guardar');
      else this.verMotivo = true;
    }
  }
};
</script>

<style scoped>
/* La paleta de la maqueta, en variables sobre el contenedor: los componentes de dentro las heredan
   aunque tengan su propio `scoped`, que es lo que hace que claro y oscuro se decidan en un solo sitio. */
.cdn{
  --navy:#002653; --leaf:#4B8A2B;
  --bg:#F3F6F9; --surface:#FFFFFF; --surface-2:#EEF2F6; --line:#D9E1EA;
  --fg:#0F2338; --muted:#5B6B7D; --faint:#8696A7;
  --brand:#002653; --brand-fg:#FFFFFF; --accent:#4B8A2B; --accent-soft:#E7F1E1;
  --heat:#C2410C; --heat-soft:#FDEBDD; --cold:#0E6BA8; --cold-soft:#E0EFF8;
  --warn:#9A6700; --warn-soft:#FFF4D6; --bad:#B42318; --bad-soft:#FDE7E5;
  --result:#002653; --result-fg:#FFFFFF; --result-muted:#9FB3C8; --result-line:rgba(255,255,255,.12);
  --f-display:"Sora","Segoe UI",system-ui,sans-serif;
  --f-body:"Manrope","Segoe UI",system-ui,sans-serif;
  --f-mono:"IBM Plex Mono",ui-monospace,"Cascadia Mono",monospace;
  --r:10px;
}
/* Oscuro: el del sistema, como en la maqueta, o el tema oscuro de Framework7 si algún día se activa. */
@media (prefers-color-scheme: dark){
  .cdn{
    --bg:#0B1724; --surface:#112234; --surface-2:#16293D; --line:#24384E;
    --fg:#E6EDF4; --muted:#9FB0C2; --faint:#71849A;
    --brand:#0A2F5C; --accent:#7DBA5A; --accent-soft:#1C3220;
    --heat:#F08A4B; --heat-soft:#3A2016; --cold:#5DB0E6; --cold-soft:#11314A;
    --warn:#E8B341; --warn-soft:#3A2E10; --bad:#F2877D; --bad-soft:#3E1A17;
    --result:#06192E; --result-muted:#8EA4BA; --result-line:rgba(255,255,255,.10);
    color-scheme:dark;
  }
}
.theme-dark .cdn{
  --bg:#0B1724; --surface:#112234; --surface-2:#16293D; --line:#24384E;
  --fg:#E6EDF4; --muted:#9FB0C2; --faint:#71849A;
  --brand:#0A2F5C; --accent:#7DBA5A; --accent-soft:#1C3220;
  --heat:#F08A4B; --heat-soft:#3A2016; --cold:#5DB0E6; --cold-soft:#11314A;
  --warn:#E8B341; --warn-soft:#3A2E10; --bad:#F2877D; --bad-soft:#3E1A17;
  --result:#06192E; --result-muted:#8EA4BA; --result-line:rgba(255,255,255,.10);
  color-scheme:dark;
}
.cdn{position:absolute;top:0;right:0;bottom:0;left:0;display:flex;flex-direction:column;background:var(--bg);color:var(--fg);font-family:var(--f-body);font-size:14px;line-height:1.45}
.cdn >>> *{box-sizing:border-box}
.cdn >>> button,.cdn >>> input,.cdn >>> select{font:inherit;color:inherit}
/* Framework7 da `width:100%` a todo `<button>`: sin esto cada botón del diseño ocupa una fila entera
   (es lo que apilaba la cabecera en la prueba de Dirección del 26/10/2026). */
.cdn >>> button{width:auto}
.cdn >>> :focus-visible{outline:2px solid var(--accent);outline-offset:2px}

/* Cabecera: una franja de 60 px, azul de lado a lado. A la izquierda el logotipo en su recuadro
   blanco y el nombre de la herramienta; a la derecha todos los botones en una fila, con «Guardar en
   línea» destacado al final. Queda fija arriba y lo que se desplaza es el cuerpo. */
.cdn-bar{flex:none;display:flex;align-items:center;justify-content:space-between;min-height:60px;padding:0 14px 0 12px;padding-top:env(safe-area-inset-top,0px);background:var(--brand);color:var(--brand-fg);position:relative;z-index:3}
.cdn-id{display:flex;align-items:center;min-width:0;flex:1 1 auto;margin-right:16px}
.cdn-logo{flex:none;background:#FFFFFF;border-radius:8px;padding:3px 6px;display:flex;align-items:center;margin-right:12px}
.cdn-logo img{height:40px;width:auto;display:block}
.cdn-who{display:flex;flex-direction:column;min-width:0}
.cdn-who b,.cdn-who span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cdn-who b{font-family:var(--f-display);font-size:15px;font-weight:600;letter-spacing:.01em;line-height:1.25}
.cdn-who span{font-size:11px;opacity:.7;line-height:1.3}
.cdn-tools{flex:none;display:flex;flex-wrap:nowrap;align-items:center}
.cdn-tools > *{margin-left:6px}
.cdn-tools > *:first-child{margin-left:0}
.cdn-tb{border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.06);color:#fff;border-radius:999px;height:30px;padding:0 12px;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;white-space:nowrap}
.cdn-tb:hover{background:rgba(255,255,255,.16)}
.cdn-tb.ghost{border-color:transparent;background:rgba(255,255,255,.08)}
.cdn-tb .dot,.cdn-mas-menu .dot{width:7px;height:7px;border-radius:50%;margin-right:6px;flex:none}
.cdn-tb.primary{background:var(--leaf);border-color:var(--leaf);margin-left:12px}
.cdn-tb.primary.off{opacity:.55;cursor:not-allowed}
.cdn-guardar,.cdn-mas{position:relative;display:inline-flex}
.cdn-why{position:absolute;right:0;top:100%;margin-top:8px;width:300px;max-width:80vw;background:var(--surface);color:var(--fg);border:1px solid var(--line);border-radius:8px;padding:8px 10px;font-size:12px;font-weight:400;line-height:1.4;box-shadow:0 6px 20px rgba(3,14,28,.25);white-space:normal}
.cdn-mas-menu{position:absolute;right:0;top:100%;margin-top:6px;min-width:160px;background:var(--surface);border:1px solid var(--line);border-radius:8px;padding:4px;box-shadow:0 6px 20px rgba(3,14,28,.25);display:flex;flex-direction:column}
.cdn-mas-menu button{border:0;background:transparent;color:var(--fg);text-align:left;padding:8px 10px;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center}
.cdn-mas-menu button:hover{background:var(--surface-2)}
/* En un móvil ni la fila corta cabe al lado del título: pasa debajo, en filas de botones. */
@media (max-width:600px){
  .cdn-bar{flex-wrap:wrap;padding-bottom:8px}
  .cdn-id{flex:1 1 100%;margin:8px 0}
  .cdn-tools{flex:1 1 100%;flex-wrap:wrap;justify-content:flex-end}
  .cdn-tools > *{margin:4px 0 0 6px}
  .cdn-who span{display:none}
}

.cdn-body{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch}
.cdn-calc{display:grid;grid-template-columns:minmax(0,1fr) 360px;gap:18px;align-items:start;max-width:1360px;margin:0 auto;padding:18px 16px 40px}
.cdn-col{display:flex;flex-direction:column;gap:14px;min-width:0}
.cdn-side{position:sticky;top:12px;min-width:0}
@media (max-width:980px){.cdn-calc{grid-template-columns:minmax(0,1fr)}.cdn-side{position:static}}

/* Las piezas de formulario comunes a todos los paneles. Viven aquí, y no repetidas en cada
   componente, para que un panel nuevo de #470 se vea igual que los de RITE sin copiar estilos. */
.cdn >>> .cdn-grid{display:grid;gap:10px 12px;grid-template-columns:repeat(auto-fit,minmax(150px,1fr))}
.cdn >>> .cdn-f{display:flex;flex-direction:column;gap:4px;min-width:0}
.cdn >>> .cdn-f label{font-size:11.5px;font-weight:600;color:var(--muted)}
.cdn >>> .cdn-f input,.cdn >>> .cdn-f select{border:1px solid var(--line);background:var(--surface-2);border-radius:7px;padding:8px 10px;width:100%;min-width:0;color:var(--fg)}
.cdn >>> .cdn-f input:focus,.cdn >>> .cdn-f select:focus{border-color:var(--accent);background:var(--surface);outline:none}
.cdn >>> .cdn-f input:disabled{opacity:.6}
.cdn >>> .cdn-f .cdn-ht{font-size:11px;color:var(--faint)}
.cdn >>> .cdn-unit{position:relative}
.cdn >>> .cdn-unit span{position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:11px;color:var(--faint);pointer-events:none}
.cdn >>> .cdn-unit input{padding-right:52px}
.cdn >>> .cdn-note{font-size:12px;color:var(--muted);margin:8px 0 0}
.cdn >>> .cdn-checks{display:flex;flex-wrap:wrap;gap:8px}
.cdn >>> .cdn-chk{display:inline-flex;align-items:center;gap:8px;border:1px solid var(--line);border-radius:8px;padding:7px 11px;cursor:pointer;background:var(--surface);font-size:13px;color:var(--fg)}
.cdn >>> .cdn-chk input{accent-color:var(--accent);width:16px;height:16px;margin:0}
.cdn >>> .cdn-chk.on{border-color:var(--accent);background:var(--accent-soft)}
.cdn >>> .cdn-btn{border:1px solid var(--line);background:var(--surface);color:var(--fg);border-radius:7px;padding:5px 10px;font-size:12px;font-weight:600;cursor:pointer}
.cdn >>> .cdn-btn:hover{border-color:var(--accent)}
.cdn >>> .cdn-btn.solid{background:var(--accent);border-color:var(--accent);color:#fff}
.cdn >>> .cdn-two{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
@media (max-width:640px){.cdn >>> .cdn-two{grid-template-columns:minmax(0,1fr)}}
/* Avisos: «ok» en verde, «miss» en ámbar. Los usan el estado de los datos y el aviso de los 70 kW. */
.cdn >>> .cdn-state{display:flex;gap:10px;align-items:flex-start;border-radius:8px;padding:9px 12px;font-size:13px;color:var(--fg)}
.cdn >>> .cdn-state + .cdn-state{margin-top:8px}
.cdn >>> .cdn-state.ok{background:var(--accent-soft)}
.cdn >>> .cdn-state.miss{background:var(--warn-soft)}
.cdn >>> .cdn-state b{display:block}
.cdn >>> .cdn-state .ico{width:18px;height:18px;border-radius:50%;flex:none;display:grid;place-items:center;font-size:11px;font-weight:800;color:#fff;margin-top:1px}
.cdn >>> .cdn-state.ok .ico{background:var(--accent)}
.cdn >>> .cdn-state.miss .ico{background:var(--warn)}
.cdn >>> .cdn-state .cdn-note{display:block;margin:2px 0 0}
</style>
